'use client';

import React, { FC, useEffect, useRef } from 'react';
import { useBreakpoint } from '@cat-ecom/pcc-components';
import { PAGE_BREAKPOINTS } from '@cat-ecom/pcc-components/src/constants/commonConstants';
import { Equipment } from 'services/equipment/types';
import AlternatePartCard from '../alternate-part-card';
import AlternatePartCardLabels from '../alternate-part-labels';
import { DealerResponse } from 'services/dealer';
import { LocationResponse } from 'services/location/types';
import { CartResponse } from 'services/cart/types';
import { ALTERNATE_PARTS_LIST } from '../../../../constants/constants';
import { useViewItemListEvent } from 'hooks/analytics/use-view-item-list-event';
import styles from '../alternate-parts.module.scss';

interface AlternatePartGridProps {
  alternatePriceAndAvailability;
  isCatCorp: boolean;
  selectedEquipment: Equipment;
  defaultUom: string;
  locale: string;
  userStoreLocationData: LocationResponse;
  availRemoveInterimStockFlag: boolean;
  dealerInformation: DealerResponse;
  isShowSourcingDetailEnabled: boolean;
  isDeliveryEnabled: boolean;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
  storeLocationId: string;
  cartDetails: CartResponse;
}

const AlternatePartGrid: FC<AlternatePartGridProps> = ({
  alternatePriceAndAvailability,
  isCatCorp,
  defaultUom,
  selectedEquipment,
  locale,
  userStoreLocationData,
  availRemoveInterimStockFlag,
  dealerInformation,
  isShowSourcingDetailEnabled,
  isDeliveryEnabled,
  isRTL = false,
  currencyLdFeatureFlag = false,
  storeLocationId,
  cartDetails
}) => {
  const isDesktop = useBreakpoint(PAGE_BREAKPOINTS.LG);
  const currentAttr =
    alternatePriceAndAvailability?.length > 0 &&
    alternatePriceAndAvailability[0]?.attributes;

  const isPartSerialCompatible = alternatePriceAndAvailability.some(
    (part) => part.isSerialCompatible
  );

  const { fireEvent: fireViewItemListEvent } = useViewItemListEvent();
  const hasViewListItem = useRef(false);

  const returnAlternatePartCard = (
    altItem,
    currentAttr,
    isPartSerialCompatible,
    index
  ) => {
    return (
      <div
        className={`d-flex ${index == 0 ? styles['cards__current-item'] : styles['cards__alternate-item']}`}
        key={altItem?.partNumber}
      >
        <AlternatePartCard
          product={altItem}
          isCoreDeposite={isCoreDeposite}
          currentSpecs={currentAttr}
          isPartSerialCompatible={isPartSerialCompatible}
          isCatCorp={isCatCorp}
          selectedEquipment={selectedEquipment}
          defaultUom={defaultUom}
          locale={locale}
          userStoreLocationData={userStoreLocationData}
          availRemoveInterimStockFlag={availRemoveInterimStockFlag}
          dealerInformation={dealerInformation}
          isShowSourcingDetailEnabled={isShowSourcingDetailEnabled}
          isDeliveryEnabled={isDeliveryEnabled}
          isRTL={isRTL}
          currencyLdFeatureFlag={currencyLdFeatureFlag}
          storeLocationId={storeLocationId}
          cartDetails={cartDetails}
        />
      </div>
    );
  };

  const returnAlternatePartLabels = (altLabelItem) => {
    return (
      <div className={`${styles['cards__alternate-item']}`}>
        <AlternatePartCardLabels
          product={altLabelItem}
          selectedEquipment={selectedEquipment}
          isPartSerialCompatible={isPartSerialCompatible}
          defaultUom={defaultUom}
          isCatCorp={isCatCorp}
          isCoreDeposite={isCoreDeposite}
        />
      </div>
    );
  };

  useEffect(() => {
    const alternateViewItemList = {
      item_list_id: ALTERNATE_PARTS_LIST,
      item_list_name: ALTERNATE_PARTS_LIST
    };
    const viewRelatedPayload = {
      products: alternatePriceAndAvailability,
      locationId: storeLocationId,
      itemListData: alternateViewItemList,
      cartItemsData: cartDetails,
      cartId: cartDetails?.orderHeader?.orderId,
      isProductsData: true
    };

    if (alternatePriceAndAvailability?.length > 0 && !hasViewListItem.current) {
      fireViewItemListEvent(viewRelatedPayload);
      hasViewListItem.current = true;
    }
  }, [
    cartDetails,
    storeLocationId,
    fireViewItemListEvent,
    alternatePriceAndAvailability
  ]);
  const isCoreDeposite = alternatePriceAndAvailability.some(
    (Item) => Item.coreCharges !== ''
  );
  return (
    <div className="d-flex">
      {isDesktop
        ? returnAlternatePartLabels(alternatePriceAndAvailability)
        : null}
      <div className={`d-flex ${styles['card-container']}`}>
        {alternatePriceAndAvailability.map((item, index) => {
          return returnAlternatePartCard(
            item,
            currentAttr,
            isPartSerialCompatible,
            index
          );
        })}
      </div>
    </div>
  );
};

export default AlternatePartGrid;
